import { useField } from 'formik';
import { clsx } from 'clsx';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
}

export interface RadioGroupProps {
  name: string;
  label?: string;
  options: RadioOption[];
  className?: string;
}

export function RadioGroup({ name, label, options, className }: RadioGroupProps) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <legend className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </legend>
      )}

      <div className="space-y-3">
        {options.map((option) => (
          <div key={option.value} className="flex items-start">
            <div className="flex h-5 items-center">
              <input
                id={`${name}-${option.value}`}
                type="radio"
                {...field}
                value={option.value}
                checked={field.value === option.value}
                className={clsx(
                  'h-5 w-5 rounded-full border transition-colors duration-200',
                  'text-primary-600 focus:ring-primary-500',
                  'focus:ring-2 focus:ring-offset-0',
                  'cursor-pointer',
                  hasError ? 'border-red-500' : 'border-gray-300 dark:border-dark-600',
                  'dark:bg-dark-800 dark:checked:bg-primary-600'
                )}
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor={`${name}-${option.value}`}
                className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {option.label}
              </label>
              {option.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{option.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {hasError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{meta.error}</p>}
    </div>
  );
}
